<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	footnotes: [{ id: 1, text: 'Footnote text here.' }],
	publishDate: '',
	lastUpdated: '',
	showDates: true,
}
const schema = defineSchema({
	footnotes: $.array(
		'Footnotes',
		defineSchema({
			id: $.text('ID'),
			text: $.text('Text', { multiline: true, rows: 2 }),
		}),
		{ collapsible: true },
	),
	publishDate: $.text('Publish Date'),
	lastUpdated: $.text('Last Updated'),
	showDates: $.toggle('Show Dates'),
})
export const FootnotesSection = defineBlock({
	pickerHidden: true,
	thumbnail: '/block-thumbs/FootnotesSection.svg',
	category: 'Content',
	order: 320,
	name: 'FootnotesSection',
	label: 'Footnotes Section',
	icon: 'format_list_numbered',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { Icon } from '@bagelink/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { sanitizeArticleHtml, sanitizeFootnotes } from '@/utils/articleHtml'




const props = withDefaults(
	defineProps<{
		footnotes?: { id: number; text: string }[] | string
		publishDate?: string
		lastUpdated?: string
		showDates?: boolean
	}>(),
	{
		footnotes: () => [],
		showDates: true,
	},
)

// Footnotes functionality
const showFootnotes = ref(false)
const tooltipElement = ref<HTMLElement | null>(null)

function toggleFootnotes() {
	showFootnotes.value = !showFootnotes.value
}

function plainFootnoteText(text: string) {
	const container = document.createElement('div')
	container.innerHTML = sanitizeArticleHtml(text)
	return container.textContent?.trim() || ''
}

const safeFootnotes = computed(() => sanitizeFootnotes(props.footnotes))

/**
 * Each footnote record starts with its own marker — either bare (`[7]`) or wrapped
 * in the back-link the article export produces
 * (`<a href="#_ftnref7" name="_ftn7">[7]</a>`). The list renders as an `<ol>`, so
 * the number is already printed; keeping the marker produced a duplicated
 * "7. [7] …" (the `#_ftnref7` anchor has no target on this page anyway).
 */
function stripFootnoteMarker(html: string, id: number) {
	const marker = `\\[\\s*${id}\\s*\\]`
	return html.replace(
		new RegExp(
			`^(\\s*<(?:p|li)\\b[^>]*>)?\\s*(?:<a\\b[^>]*>\\s*${marker}\\s*</a>|${marker})[.):]?\\s*`,
			'i',
		),
		(_full, tag?: string) => tag || '',
	)
}

function normalizedFootnotes(): Array<{ id: number; text: string }> {
	if (Array.isArray(safeFootnotes.value)) {
		return safeFootnotes.value.map(note => ({
			id: Number(note.id),
			text: stripFootnoteMarker(note.text || '', Number(note.id)),
		}))
	}
	if (typeof document === 'undefined') {
		return safeFootnotes.value
			.split(/(?=<p\b[^>]*>\s*(?:<a\b[^>]*>\s*)?\[\d+\])/i)
			.reduce<Array<{ id: number; text: string }>>((notes, chunk) => {
				const match = chunk.match(/^<p\b[^>]*>\s*(?:<a\b[^>]*>\s*)?\[(\d+)\]/i)
				if (match)
					notes.push({ id: Number(match[1]), text: stripFootnoteMarker(chunk, Number(match[1])) })
				return notes
			}, [])
	}
	const container = document.createElement('div')
	container.innerHTML = safeFootnotes.value
	const result: Array<{ id: number; text: string }> = []
	let current: { id: number; parts: string[] } | undefined
	for (const child of Array.from(container.children)) {
		const text = child.textContent?.trim() || ''
		const match = text.match(/^\[(\d+)\]/)
		if (match) {
			if (current) result.push({ id: current.id, text: current.parts.join('') })
			current = {
				id: Number(match[1]),
				parts: [stripFootnoteMarker(child.outerHTML, Number(match[1]))],
			}
		} else if (current) current.parts.push(child.outerHTML)
	}
	if (current) result.push({ id: current.id, text: current.parts.join('') })
	return result
}

function getFootnoteText(id: number) {
	const footnote = normalizedFootnotes().find(f => Number(f.id) === id)
	return footnote?.text || ''
}

function formatFootnoteDate(value?: string) {
	if (!value) return ''
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return value
	return date.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
		timeZone: 'UTC',
	})
}

const formattedPublishDate = computed(() => formatFootnoteDate(props.publishDate))
const formattedLastUpdated = computed(() => formatFootnoteDate(props.lastUpdated))

function footnoteMarkerId(text: string) {
	const marker = text.trim()
	return /^\[\d+\]$/.test(marker) ? Number(marker.match(/\d+/)?.[0]) : 0
}

/** Plain-text twin of `stripFootnoteMarker` for the tooltip body. */
function stripPlainMarker(text: string, id: number) {
	return text.replace(new RegExp(`^(?:(?:\\[\\s*${id}\\s*\\]|${id}\\s*[.)])[.):]?\\s*)+`, 'i'), '')
}

function promoteBareFootnotes(root: ParentNode) {
	const ids = new Set(normalizedFootnotes().map(note => Number(note.id)))
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
	const targets: Array<{ node: Text; id: number }> = []
	let current = walker.nextNode()
	while (current) {
		const node = current as Text
		current = walker.nextNode()
		const parent = node.parentElement
		if (!parent || parent.closest('sup, a, .footnotes-section, script, style')) continue
		const match = node.data.match(/(?:^|[\s.,;:])\[(\d+)\]$/)
		if (!match || !ids.has(Number(match[1]))) continue
		targets.push({ node, id: Number(match[1]) })
	}
	for (const target of targets) {
		const token = `[${target.id}]`
		const start = target.node.data.lastIndexOf(token)
		if (start < 0) continue
		const tail = target.node.splitText(start)
		tail.data = tail.data.slice(token.length)
		const sup = document.createElement('sup')
		sup.textContent = token
		tail.parentNode?.insertBefore(sup, tail)
	}
}

function showFootnoteTooltip(element: Element, footnoteId: number) {
	const footnoteText = getFootnoteText(footnoteId)
	if (!footnoteText) return

	hideFootnoteTooltip()

	const tooltip = document.createElement('div')
	const label = document.createElement('strong')
	const body = stripPlainMarker(plainFootnoteText(footnoteText), footnoteId)
	label.textContent = `${footnoteId}.`
	tooltip.append(label, document.createTextNode(` ${body}`))
	tooltip.className = 'footnote-tooltip'
	tooltip.style.visibility = 'hidden'
	tooltip.style.position = 'absolute'
	tooltip.style.top = '0px'
	tooltip.style.left = '0px'

	document.body.appendChild(tooltip)

	const tooltipRect = tooltip.getBoundingClientRect()
	const tooltipHeight = tooltipRect.height

	const rect = element.getBoundingClientRect()
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop
	const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

	const spaceAbove = rect.top
	const spaceBelow = window.innerHeight - rect.bottom
	const isMobile = window.innerWidth <= 768

	if (isMobile) {
		const showAbove = spaceAbove >= tooltipHeight + 20 && spaceAbove >= spaceBelow

		tooltip.className = showAbove
			? 'footnote-tooltip mobile-arrow-above'
			: 'footnote-tooltip mobile-arrow-below'
		tooltip.style.position = 'absolute'
		tooltip.style.left = '10px'
		tooltip.style.right = '10px'
		tooltip.style.transform = 'none'

		if (showAbove) {
			tooltip.style.top = `${rect.top + scrollTop - tooltipHeight - 10}px`
		} else {
			tooltip.style.top = `${rect.bottom + scrollTop + 10}px`
		}

		const arrowLeft = Math.max(
			20,
			Math.min(window.innerWidth - 40, rect.left + rect.width / 2 - 10),
		)
		tooltip.style.setProperty('--arrow-left', `${arrowLeft}px`)
	} else {
		const showAbove =
			spaceAbove >= tooltipHeight + 20 ||
			(spaceBelow < tooltipHeight + 20 && spaceAbove > spaceBelow)
		const half = tooltipRect.width / 2
		const center = Math.min(
			window.innerWidth - half - 12,
			Math.max(half + 12, rect.left + rect.width / 2),
		)

		tooltip.className = showAbove
			? 'footnote-tooltip tooltip-above'
			: 'footnote-tooltip tooltip-below'
		tooltip.style.left = `${center + scrollLeft}px`
		tooltip.style.top = showAbove
			? `${Math.max(scrollTop + 8, rect.top + scrollTop - tooltipHeight - 10)}px`
			: `${rect.bottom + scrollTop + 10}px`
		tooltip.style.transform = 'translateX(-50%)'
	}

	tooltip.style.visibility = 'visible'
	tooltipElement.value = tooltip
}

function hideFootnoteTooltip() {
	if (tooltipElement.value) {
		document.body.removeChild(tooltipElement.value)
		tooltipElement.value = null
	}
}

function addFootnoteTooltips() {
	const article = document.querySelector('.article-page')
	if (article) promoteBareFootnotes(article)
	const supElements = document.querySelectorAll(
		'.article-page .sym-rtf sup, .article-page .sym-rtf .hebQuote sup, .article-page .sym-rtf .enQuote sup',
	)
	supElements.forEach(sup => {
		if ((sup as HTMLElement).dataset.footnoteReady === 'true') return
		// Verse labels inside bilingual quotes and biblical tables are also <sup>,
		// but only a bracketed marker (`[5]`) is a footnote. The footnotes list
		// itself must stay inert.
		if (sup.closest('.footnotes-section')) return
		if (
			sup.closest('.biblical-quote-table, .hebQuote, .enQuote') &&
			!/^\[\d+\]$/.test((sup.textContent || '').trim())
		) {
			return
		}

		const footnoteId = footnoteMarkerId(sup.textContent || '')
		if (footnoteId && normalizedFootnotes().length) {
			sup.textContent = `[${footnoteId}]`

			sup.addEventListener('mouseenter', () => {
				showFootnoteTooltip(sup, footnoteId)
			})
			sup.addEventListener('mouseleave', hideFootnoteTooltip)
			;(sup as HTMLElement).style.cursor = 'pointer'
			;(sup as HTMLElement).style.color = 'var(--bgl-primary)'
			;(sup as HTMLElement).dataset.footnoteReady = 'true'
		}
	})
}

onMounted(() => {
	nextTick(() => {
		addFootnoteTooltips()
	})
})

watch(
	() => props.footnotes,
	() => nextTick(addFootnoteTooltips),
)
onBeforeUnmount(hideFootnoteTooltip)
</script>

<template>
	<div v-if="normalizedFootnotes().length" class="footnotes-section w720px mx-auto pb-1">
		<button
			type="button"
			class="footnotes-header cursor-pointer flex align-items-center gap-025 py-05 hover bg-transparent border-none px-0"
			@click="toggleFootnotes"
		>
			<h3 class="m-0 color-primary txt20 regular">View Footnotes</h3>
			<Icon
				name="chevron_right"
				class="color-primary transition hidePrint"
				size="1.5"
				weight="300"
				:class="{ 'rotate-90': showFootnotes }"
			/>
		</button>

		<div class="footnotes-content" :class="{ showFootnotes }">
			<div
				v-if="showDates && (formattedPublishDate || formattedLastUpdated)"
				class="color-gray-70 flex gap-1 light txt14"
			>
				<p v-if="formattedPublishDate">Published {{ formattedPublishDate }}</p>
				<p v-if="formattedPublishDate && formattedLastUpdated">|</p>
				<p v-if="formattedLastUpdated">Last Updated {{ formattedLastUpdated }}</p>
			</div>
			<ol class="footnotes-list txt15 color-black-90">
				<li v-for="footnote in normalizedFootnotes()" :key="footnote.id">
					<span v-html="footnote.text" class="line-height-2 block" />
				</li>
			</ol>
		</div>
	</div>
</template>

<style scoped>
.footnotes-content {
	display: grid;
	grid-template-rows: 0fr;
	transition: grid-template-rows 0.3s ease-in-out;
}

.footnotes-content.showFootnotes {
	grid-template-rows: 1fr;
}

.footnotes-content > div {
	overflow: hidden;
	min-height: 0;
}

.footnotes-header {
	color: inherit;
	font: inherit;
	text-align: start;
}

.footnotes-header:focus {
	outline: none;
}

.footnotes-header:focus-visible {
	outline: 2px solid var(--bgl-primary);
	outline-offset: 2px;
}

.footnotes-list {
	margin: 0.75rem 0 0;
	padding-inline-start: 1.35rem;
}

.footnotes-list li {
	padding-bottom: 0.5rem;
	font-weight: 300;
	line-height: 1.55;
}
</style>
